import { IFilter, IFinancialReportFilter } from 'props';
import dayjs from 'dayjs';
import api from '../services/api/axios';
import { ImportType } from '../types/enums';
import { IReportGenerationViewModel } from 'viewModels';

const download = (data: any, name: string) => {
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const uri = window.URL.createObjectURL(blob);

  if (!uri) return;
  const link = document.createElement('a');

  link.href = uri;
  link.setAttribute('download', name);
  link.rel = 'noopener noreferrer';
  link.referrerPolicy = 'no-referrer';
  link.click();

  link.parentElement?.removeChild(link);
  window.URL.revokeObjectURL(uri);
};

export const getTemplate = (name: string) => {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`report`, { params: { name }, responseType: 'blob' })
      .then(({ data }) => {
        download(data, `modelo-importação-${name?.toLowerCase() ?? 'template'}`);
        resolve();
      })
      .catch(reject);
  });
};

export const exportSolicitations = (input: IFilter) => {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`report/export-solicitations`, input, {
        responseType: 'blob',
      })
      .then(({ data }) => {
        download(data, `solicitações-${Date.now()}`);
        resolve();
      })
      .catch(reject);
  });
};

export const exportSolicitationsAsync = (input: IFilter) => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/export-solicitations-async`, input)
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};
export const exportPayments = (input: IFinancialReportFilter) => {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`report/export-payments`, input, {
        responseType: 'blob',
      })
      .then(({ data }) => {
        download(data, `pagamentos-${Date.now()}`);
        resolve();
      })
      .catch(reject);
  });
};
export const exportCollaborator = (companyId: string) => {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`report/export-collaborators/${companyId}`, {
        responseType: 'blob',
      })
      .then(({ data }) => {
        download(data, `colaboradores-${Date.now()}`);
        resolve();
      })
      .catch(reject);
  });
};

export const exportCorrespondents = (startDate?: Date, endDate?: Date) => {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`report/export-correspondent`, {
        params: {
          startDate,
          endDate,
        },
        responseType: 'blob',
      })
      .then(({ data }) => {
        download(data, `correspondentes-${Date.now()}`);
        resolve();
      })
      .catch(reject);
  });
};
export const exportSolicitationDashboard = (startDate: Date, endDate: Date) => {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`report/export-solicitation-dashboard`, {
        params: {
          startDate,
          endDate,
        },
        responseType: 'blob',
      })
      .then(({ data }) => {
        download(data, `correspondentes-${Date.now()}`);
        resolve();
      })
      .catch(reject);
  });
};

export const exportSolicitationsByCreator = (startDate: Date, endDate: Date) => {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`report/export-solicitations-by-creator`, {
        params: {
          startDate,
          endDate,
        },
        responseType: 'blob',
      })
      .then(({ data }) => {
        download(data, `solicitacoes-por-criador-${Date.now()}`);
        resolve();
      })
      .catch(reject);
  });
};

export const exportCompletedSolicitationsByCorrespondent = (startDate: Date, endDate: Date) => {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`report/export-completed-solicitations-by-correspondent`, {
        params: {
          startDate,
          endDate,
        },
        responseType: 'blob',
      })
      .then(({ data }) => {
        download(data, `solicitacoes-concluidas-por-correspondente-${Date.now()}`);
        resolve();
      })
      .catch(reject);
  });
};

export const exportAllUsers = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
  return new Promise<void>((resolve, reject) => {
    api
      .get(
        `report/export-user-movements?startDate=${startDate.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]
        }`,
        {
          responseType: 'blob',
        },
      )
      .then(({ data }) => {
        download(data, `allUsers-${Date.now()}`);
        resolve();
      })
      .catch(reject);
  });
};

export const exportUsersIncomplete = () => {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`report/export-incomplete`, {
        responseType: 'blob',
      })
      .then(({ data }) => {
        download(data, `incomplete-${Date.now()}`);
        resolve();
      })
      .catch(reject);
  });
};

export const exportDemands = (input: IFilter) => {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`report/export-demands`, input, {
        responseType: 'blob',
      })
      .then(({ data }) => {
        download(data, `demandas-${Date.now()}`);
        resolve();
      })
      .catch(reject);
  });
};

export const importXLSX = (type: ImportType, file: File) =>
  new Promise<void>((resolve, reject) => {
    api
      .post(`report`, { type, file }, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => resolve())
      .catch(reject);
  });

// Novos métodos para geração de relatórios em background
export const requestSolicitationsReport = (input: IFilter) => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/request-solicitations-report`, input)
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};

export const requestAllSolicitationsReport = () => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/request-all-solicitations-report`)
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};

export const requestPaymentsReport = (input: IFinancialReportFilter) => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/request-payments-report`, input)
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};

export const requestDemandsReport = (input: IFilter) => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/request-demands-report`, input)
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};

export const requestCollaboratorsReport = (companyId: string) => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/request-collaborators-report`, null, { params: { companyId } })
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};

export const requestCorrespondentsReport = (startDate: Date, endDate: Date) => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/request-correspondents-report`, null, {
        params: { startDate, endDate },
      })
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};

export const requestIncompleteRegistersReport = (startDate: Date, endDate: Date) => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/request-incomplete-registers-report`, null, {
        params: { startDate, endDate },
      })
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};

export const requestWhatsAppContactsReport = () => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/request-whatsapp-contacts-report`)
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};

export const requestDashboardSolicitationsReport = (startDate: Date, endDate: Date) => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/request-dashboard-solicitations-report`, null, {
        params: { startDate, endDate },
      })
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};

export const requestUserMovementsReport = (startDate?: Date, endDate?: Date) => {
  return new Promise<string>((resolve, reject) => {
    api
      .post(`report/request-user-movements-report`, null, {
        params: { startDate, endDate },
      })
      .then(({ data }) => {
        resolve(data.reportId);
      })
      .catch(reject);
  });
};

export const getReportStatus = (reportId: string) => {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`report/report-status/${reportId}`)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(reject);
  });
};

export const getReportDownloadUrl = (reportId: string) => {
  return new Promise<IReportGenerationViewModel>((resolve, reject) => {
    api
      .get(`report/report-download-url/${reportId}`)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(reject);
  });
};

export const downloadReportById = (reportId: string) => {
  return new Promise<void>((resolve, reject) => {
    getReportDownloadUrl(reportId)
      .then((reportData) => {
        // Download direto do Azure usando a URL SAS
        const link = document.createElement('a');
        link.href = reportData.downloadUrl;
        link.setAttribute('download', reportData.fileName || `relatorio-${Date.now()}.xlsx`);
        link.rel = 'noopener noreferrer';
        link.referrerPolicy = 'no-referrer';
        link.target = '_blank'; // Abre em nova aba se o navegador bloquear o download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        resolve();
      })
      .catch(reject);
  });
};